import './as-option.ts';
import './as-help-hover.ts';
import { updateShadowDOMHelpHover } from './input-help-hover-utils.ts';
import styleText from './as-dropdown.css?raw';
import type { SetValueOptions } from './types';
import { resolveSetValueSilence, upgradeProperty } from './utils';

type OptionElement = HTMLElement & {
  value?: string;
  label?: string;
  disabled?: boolean;
};

type SelectionUpdateOptions = {
  emitInput?: boolean;
  emitChange?: boolean;
  reflect?: boolean;
  focus?: boolean;
};

const template = document.createElement('template');
template.innerHTML = /*html*/`
  <div class="as-dropdown-label-container">
    <label class="as-dropdown-label"></label>
    <span class="help-hover-container"></span>
  </div>
  <div class="as-dropdown-container">
    <button
      type="button"
      class="as-dropdown-selected"
      aria-haspopup="listbox"
      aria-expanded="false"
    >
      <span class="as-dropdown-selected-text"></span>
      <span class="as-dropdown-arrow" aria-hidden="true"></span>
    </button>
    <div class="as-dropdown-list" role="listbox">
      <slot></slot>
    </div>
  </div>
`;

/**
 * as-dropdown: Custom dropdown that works with light-DOM as-option children.
 */
export class AsDropdown extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes() {
    return ['label-default-text', 'label-dict-key', 'disabled', 'hide', 'value', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement'];
  }

  private _shadowRoot?: ShadowRoot;
  private _labelEl?: HTMLLabelElement;
  private _helpHoverEl?: HTMLElement;
  private _triggerEl?: HTMLButtonElement;
  private _selectedTextEl?: HTMLElement;
  private _listEl?: HTMLElement;
  private _slotEl?: HTMLSlotElement;

  private _open = false;
  private _options: OptionElement[] = [];
  private _selectedIndex = -1;
  private _value: string | null = null;
  private _labelText = '';
  private _hoveredIndex: number | null = null;

  private _isSettingValueAttr = false;
  private _isSyncingOptions = false;
  private _optionObserver = new MutationObserver(() => {
    if (this._isSyncingOptions) {
      return;
    }
    this._syncSelectionFromOptions();
    this._updateOptionTabIndexes();
    this._updateHighlightState();
  });

  constructor() {
    super();
  }

  connectedCallback() {
    upgradeProperty(this, 'value');
    upgradeProperty(this, 'disabled');
    upgradeProperty(this, 'hide');
    this._initializeOnce();
    this._collectOptions();
    this._syncSelectionFromAttributes();
    this._updateComponentState();
    this._updateHelpHover();
    document.addEventListener('mousedown', this._handleDocumentMouseDown, true);
  }

  disconnectedCallback() {
    document.removeEventListener('mousedown', this._handleDocumentMouseDown, true);
    this._optionObserver.disconnect();
    this._options.forEach(opt => {
      opt.removeEventListener('click', this._onOptionClick);
      opt.removeEventListener('keydown', this._onOptionKeyDown);
      opt.removeEventListener('pointerenter', this._onOptionPointerEnter);
      opt.removeEventListener('pointerleave', this._onOptionPointerLeave);
      opt.removeEventListener('focus', this._onOptionFocus);
      opt.removeEventListener('blur', this._onOptionBlur);
    });
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null) {
    if (name === 'label-default-text') {
      this._labelText = newValue ?? '';
      this._updateLabelText();
    } else if (name === 'disabled') {
      if (this.disabled && this._open) {
        this._closeDropdown(false);
      }
      this._updateTriggerState();
    } else if (name === 'hide') {
      // handled via :host([hide]) selector; no-op here
    } else if (name === 'value') {
      if (this._isSettingValueAttr) {
        return;
      }

      if (newValue === null) {
        this._clearSelection({ emitInput: false, emitChange: true, reflect: false });
      } else {
        this._selectByValue(newValue, { emitInput: false, emitChange: true, reflect: false });
      }
    } else if (name === 'help-tooltip-default-text' || name === 'help-tooltip-dict-key' || name === 'help-placement') {
      this._updateHelpHover();
    }
  }

  get value() {
    return this._value;
  }

  set value(val: string | null) {
    this.setValue(val);
  }

  public setValue(val: string | null, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange } = resolveSetValueSilence(opt);
    const emitInput = !silenceInput;
    const emitChange = !silenceChange;

    if (val === null) {
      this._clearSelection({ emitInput, emitChange, reflect: true });
      return;
    }

    this._selectByValue(val, { emitInput, emitChange, reflect: true });
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  private _initializeOnce() {
    if (!this._shadowRoot) {
      this._shadowRoot = this.attachShadow({ mode: 'open' });
      this._shadowRoot.appendChild(template.content.cloneNode(true));
      this._shadowRoot.adoptedStyleSheets = [
        ...this._shadowRoot.adoptedStyleSheets,
        AsDropdown.sheet,
      ];

      this._labelEl = this._shadowRoot.querySelector('.as-dropdown-label') as HTMLLabelElement;
      this._helpHoverEl = this._shadowRoot.querySelector('.help-hover-container') as HTMLElement;
      this._triggerEl = this._shadowRoot.querySelector('.as-dropdown-selected') as HTMLButtonElement;
      this._selectedTextEl = this._shadowRoot.querySelector('.as-dropdown-selected-text') as HTMLElement;
      this._listEl = this._shadowRoot.querySelector('.as-dropdown-list') as HTMLElement;
      this._slotEl = this._shadowRoot.querySelector('slot') as HTMLSlotElement;

      this.setAttribute('role', 'combobox');
      this.setAttribute('aria-haspopup', 'listbox');

      this._triggerEl?.addEventListener('click', this._toggleDropdown);
      this._triggerEl?.addEventListener('keydown', this._onTriggerKeyDown);
      this.addEventListener('focusout', this._onFocusOut);
      this._slotEl?.addEventListener('slotchange', this._onSlotChange);
    }
  }

  private _collectOptions() {
    const assigned = this._slotEl
      ? this._slotEl.assignedElements({ flatten: true })
      : Array.from(this.querySelectorAll('as-option'));

    const newOptions = assigned.filter(
      (node): node is OptionElement => node instanceof HTMLElement && node.tagName.toLowerCase() === 'as-option'
    );

    this._options.forEach(opt => {
      opt.removeEventListener('click', this._onOptionClick);
      opt.removeEventListener('keydown', this._onOptionKeyDown);
      opt.removeEventListener('pointerenter', this._onOptionPointerEnter);
      opt.removeEventListener('pointerleave', this._onOptionPointerLeave);
      opt.removeEventListener('focus', this._onOptionFocus);
      opt.removeEventListener('blur', this._onOptionBlur);
    });

    this._optionObserver.disconnect();

    this._options = newOptions;
    this._hoveredIndex = null;
    this._options.forEach((opt, index) => {
      opt.addEventListener('click', this._onOptionClick);
      opt.addEventListener('keydown', this._onOptionKeyDown);
      opt.addEventListener('pointerenter', this._onOptionPointerEnter);
      opt.addEventListener('pointerleave', this._onOptionPointerLeave);
      opt.addEventListener('focus', this._onOptionFocus);
      opt.addEventListener('blur', this._onOptionBlur);
      opt.dataset.optionIndex = String(index);
      if (!opt.hasAttribute('value')) {
        const derivedValue = this._deriveOptionValue(opt, index);
        if (derivedValue !== '') {
          opt.setAttribute('value', derivedValue);
        }
      }
      this._optionObserver.observe(opt, {
        attributes: true,
        attributeFilter: ['selected', 'disabled', 'label', 'value'],
      });
    });

    this._updateOptionTabIndexes();
    this._updateHighlightState();
  }

  private _syncSelectionFromAttributes() {
    if (!this._options.length) {
      this._clearSelection({ emitChange: false, reflect: true });
      return;
    }

    if (this._value !== null && this._selectByValue(this._value, { emitChange: false })) {
      return;
    }

    const selectedIdx = this._options.findIndex(opt => opt.hasAttribute('selected'));
    if (selectedIdx !== -1) {
      this._setSelectedIndex(selectedIdx, { emitInput: false, emitChange: false, reflect: true });
      return;
    }

    this._selectFirstAvailable({ emitChange: false });
  }

  private _syncSelectionFromOptions() {
    if (!this._options.length) {
      this._clearSelection({ emitChange: false, reflect: true });
      return;
    }

    const selectedIdx = this._options.findIndex(opt => opt.hasAttribute('selected'));
    if (selectedIdx !== -1 && selectedIdx !== this._selectedIndex) {
      this._setSelectedIndex(selectedIdx, { emitInput: false, emitChange: false, reflect: true });
      return;
    }

    if (this._value !== null) {
      this._selectByValue(this._value, { emitChange: false });
      return;
    }

    if (this._selectedIndex === -1) {
      this._selectFirstAvailable({ emitChange: false });
    } else {
      this._updateTriggerState();
      this._updateHighlightState();
    }
  }

  private _selectFirstAvailable(options: SelectionUpdateOptions = {}) {
    const emitChange = options.emitChange ?? true;
    const emitInput = options.emitInput ?? emitChange;
    const idx = this._options.findIndex(opt => !opt.hasAttribute('disabled'));
    if (idx !== -1) {
      this._setSelectedIndex(idx, { emitInput, emitChange, reflect: true });
    } else {
      this._clearSelection({ emitInput, emitChange, reflect: true });
    }
  }

  private _selectByValue(
    val: string | null,
    options: SelectionUpdateOptions = {}
  ): boolean {
    const emitChange = options.emitChange ?? true;
    const emitInput = options.emitInput ?? emitChange;
    const reflect = options.reflect ?? true;
    const focus = options.focus ?? false;

    if (val == null) {
      this._clearSelection({ emitInput, emitChange, reflect });
      return true;
    }

    const idx = this._options.findIndex(opt => this._getOptionValue(opt) === val);
    if (idx === -1) {
      return false;
    }

    this._setSelectedIndex(idx, { emitInput, emitChange, reflect, focus });
    return true;
  }

  private _setSelectedIndex(
    index: number,
    options: SelectionUpdateOptions = {}
  ): void {
    const emitChange = options.emitChange ?? true;
    const emitInput = options.emitInput ?? emitChange;
    const reflect = options.reflect ?? true;
    const focus = options.focus ?? false;

    if (index < 0 || index >= this._options.length) {
      this._clearSelection({ emitInput, emitChange, reflect });
      return;
    }

    const option = this._options[index];
    if (option.hasAttribute('disabled')) {
      return;
    }

    const previousIndex = this._selectedIndex;
    const previousOption = previousIndex >= 0 ? this._options[previousIndex] : null;
    const previousValue = this._value;

    this._isSyncingOptions = true;
    this._options.forEach((opt, i) => {
      const shouldSelect = i === index;
      if (opt.hasAttribute('selected') !== shouldSelect) {
        if (shouldSelect) {
          opt.setAttribute('selected', '');
        } else {
          opt.removeAttribute('selected');
        }
      }
      opt.setAttribute('aria-selected', shouldSelect ? 'true' : 'false');
    });
    this._isSyncingOptions = false;

    this._selectedIndex = index;
    const newValue = this._getOptionValue(option);
    this._updateValueInternal(newValue, {
      emitInput: emitInput && newValue !== previousValue,
      emitChange: emitChange && newValue !== previousValue,
      reflect,
      option,
      previousOption,
    });
    this._updateTriggerState();
    this._updateHighlightState();

    if (focus) {
      this._focusOption(index);
    }
  }

  private _updateValueInternal(
    value: string | null,
    options: {
      emitInput: boolean;
      emitChange: boolean;
      reflect: boolean;
      option?: OptionElement | null;
      previousOption?: OptionElement | null;
    }
  ): void {
    const previous = this._value;
    this._value = value;

    if (options.reflect) {
      this._isSettingValueAttr = true;
      if (value === null) {
        this.removeAttribute('value');
      } else {
        this.setAttribute('value', value);
      }
      this._isSettingValueAttr = false;
    }

    const option = options.option ?? (this._selectedIndex >= 0 ? this._options[this._selectedIndex] : null);
    const label = this._getOptionLabel(option);
    const previousLabel = this._getOptionLabel(options.previousOption);

    if (options.emitInput && previous !== value) {
      this._emitInput(value, previous, label, previousLabel);
    }

    if (options.emitChange && previous !== value) {
      this._emitChange(value, label);
    }
  }

  private _emitInput(
    value: string | null,
    previousValue: string | null,
    label: string | null,
    previousLabel: string | null
  ): void {
    const detail = {
      type: 'dropdown' as const,
      value,
      previousValue,
      label,
      previousLabel,
      selectedIndex: this._selectedIndex,
      phase: 'input' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-input', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private _emitChange(value: string | null, label: string | null): void {
    const detail = {
      type: 'dropdown' as const,
      value,
      label,
      selectedIndex: this._selectedIndex,
      phase: 'change' as const,
    };
    this.dispatchEvent(
      new CustomEvent('as-input-change', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
    this.dispatchEvent(new CustomEvent('change', { detail: { value } }));
  }

  private _getOptionLabel(option?: OptionElement | null): string | null {
    if (!option) {
      return null;
    }
    return option.getAttribute('label') ?? option.textContent?.trim() ?? null;
  }

  private _clearSelection(options: SelectionUpdateOptions = {}) {
    const emitChange = options.emitChange ?? true;
    const emitInput = options.emitInput ?? emitChange;
    const reflect = options.reflect ?? true;

    const previousValue = this._value;
    const previousIndex = this._selectedIndex;
    const previousOption = previousIndex >= 0 ? this._options[previousIndex] : null;

    this._isSyncingOptions = true;
    this._options.forEach(opt => {
      opt.removeAttribute('selected');
      opt.setAttribute('aria-selected', 'false');
    });
    this._isSyncingOptions = false;

    this._selectedIndex = -1;

    this._updateValueInternal(null, {
      emitInput: emitInput && previousValue !== null,
      emitChange: emitChange && previousValue !== null,
      reflect,
      option: null,
      previousOption,
    });

    this._updateTriggerState();
    this._updateHighlightState();
  }

  private _updateComponentState() {
    this._updateLabelText();
    this._updateTriggerState();
    this._updateOptionTabIndexes();
    this._updateListVisibility();
    this._updateHighlightState();
  }

  private _updateLabelText() {
    if (this._labelEl) {
      this._labelEl.textContent = this._labelText || this.getAttribute('label-default-text') || 'Option';
    }
  }

  private _updateTriggerState() {
    const option = this._options[this._selectedIndex];
    if (this._selectedTextEl) {
      const text =
        option?.getAttribute('label') ??
        option?.textContent?.trim() ??
        '';
      this._selectedTextEl.textContent = text;
    }

    if (this._triggerEl) {
      this._triggerEl.setAttribute('aria-expanded', this._open ? 'true' : 'false');
      this._triggerEl.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
      if (this.disabled) {
        this._triggerEl.setAttribute('disabled', '');
      } else {
        this._triggerEl.removeAttribute('disabled');
      }
    }

    this._updateListVisibility();
  }

  private _updateListVisibility() {
    if (this._listEl) {
      this.toggleAttribute('data-open', this._open);
      this._listEl.hidden = !this._open;
    }
  }

  private _updateOptionTabIndexes() {
    const allowFocus = this._open && !this.disabled;
    this._options.forEach(opt => {
      opt.tabIndex = allowFocus && !opt.hasAttribute('disabled') ? 0 : -1;
    });
  }

  private _updateHighlightState() {
    const highlightIndex = this._hoveredIndex ?? this._selectedIndex;
    this._options.forEach((opt, index) => {
      const shouldHighlight = highlightIndex === index && !opt.hasAttribute('disabled');
      opt.toggleAttribute('data-highlighted', shouldHighlight);
    });
  }

  private _setHoveredIndex(index: number | null) {
    this._hoveredIndex = index;
    this._updateHighlightState();
  }

  private _deriveOptionValue(option: OptionElement, indexOverride?: number) {
    const valueAttr = option.getAttribute('value');
    if (valueAttr != null && valueAttr.trim() !== '') {
      return valueAttr.trim();
    }
    const labelAttr = option.getAttribute('label');
    if (labelAttr && labelAttr.trim() !== '') {
      return labelAttr.trim();
    }
    const text = option.textContent?.trim();
    if (text && text !== '') {
      return text;
    }
    const idx = indexOverride ?? this._options.indexOf(option);
    return idx >= 0 ? String(idx) : '';
  }

  private _getOptionIndex(option: OptionElement): number | null {
    const directIndex = this._options.indexOf(option);
    if (directIndex !== -1) {
      return option.hasAttribute('disabled') ? null : directIndex;
    }
    const attr = option.dataset.optionIndex;
    if (attr != null) {
      const parsed = Number(attr);
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed < this._options.length) {
        return option.hasAttribute('disabled') ? null : parsed;
      }
    }
    return null;
  }

  private _openDropdown(focusSelected = true) {
    if (this.disabled || this._open) {
      return;
    }
    this._open = true;
    this._updateOptionTabIndexes();
    this._updateTriggerState();
    this._setHoveredIndex(null);
    if (focusSelected && this._selectedIndex >= 0) {
      this._focusOption(this._selectedIndex);
    }
  }

  private _closeDropdown(returnFocus = true) {
    if (!this._open) {
      return;
    }
    this._open = false;
    this._updateOptionTabIndexes();
    this._updateTriggerState();
    this._setHoveredIndex(null);
    if (returnFocus && this._triggerEl) {
      this._triggerEl.focus();
    }
  }

  private _toggleDropdown = () => {
    if (this.disabled) {
      return;
    }
    if (this._open) {
      this._closeDropdown();
    } else {
      this._openDropdown(true);
    }
  };

  private _onTriggerKeyDown = (event: KeyboardEvent) => {
    if (this.disabled) {
      return;
    }
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this._toggleDropdown();
        break;
      case 'ArrowDown':
        if (this.hasAttribute("listen-arrow")) {
          event.preventDefault();
          if (!this._open) {
            this._openDropdown(false);
          }
          this._moveSelection(1);
        } else {
          event.preventDefault();
        }
        break;
      case 'ArrowUp':
        if (this.hasAttribute("listen-arrow")) {
          event.preventDefault();
          if (!this._open) {
            this._openDropdown(false);
          }
          this._moveSelection(-1);
        } else {
          event.preventDefault();
        }
        break;
      case 'Escape':
        if (this._open) {
          event.preventDefault();
          this._closeDropdown();
        }
        break;
      default:
        break;
    }
  };

  private _onOptionKeyDown = (event: KeyboardEvent) => {
    if (this.disabled) {
      return;
    }
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this._handleOptionSelection(event.currentTarget as OptionElement);
        break;
      case 'ArrowDown':
        if (this.hasAttribute("listen-arrow")) {
          event.preventDefault();
          this._moveSelection(1);
        } else {
          event.preventDefault();
        }
        break;
      case 'ArrowUp':
        if (this.hasAttribute("listen-arrow")) {
          event.preventDefault();
          this._moveSelection(-1);
        } else {
          event.preventDefault();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this._closeDropdown();
        break;
      default:
        break;
    }
  };

  private _onOptionClick = (event: Event) => {
    if (this.disabled) {
      return;
    }
    this._handleOptionSelection(event.currentTarget as OptionElement);
  };

  private _onOptionPointerEnter = (event: Event) => {
    const option = event.currentTarget as OptionElement;
    const idx = this._getOptionIndex(option);
    if (idx !== null) {
      this._setHoveredIndex(idx);
    }
  };

  private _onOptionPointerLeave = (event: Event) => {
    const option = event.currentTarget as OptionElement;
    const idx = this._getOptionIndex(option);
    if (idx !== null && this._hoveredIndex === idx) {
      this._setHoveredIndex(null);
    }
  };

  private _onOptionFocus = (event: Event) => {
    const option = event.currentTarget as OptionElement;
    const idx = this._getOptionIndex(option);
    if (idx !== null) {
      this._setHoveredIndex(idx);
    }
  };

  private _onOptionBlur = (event: Event) => {
    const option = event.currentTarget as OptionElement;
    const idx = this._getOptionIndex(option);
    if (idx !== null && this._hoveredIndex === idx) {
      this._setHoveredIndex(null);
    }
  };

  private _handleOptionSelection(option: OptionElement) {
    const idx = this._getOptionIndex(option);
    if (idx === null || option.hasAttribute('disabled')) {
      return;
    }
    this._setSelectedIndex(idx, { emitInput: true, emitChange: true, reflect: true });
    this._closeDropdown();
  }

  private _moveSelection(delta: number) {
    if (!this._options.length) {
      return;
    }
    let idx = this._selectedIndex;
    const start = idx;
    const length = this._options.length;

    if (idx === -1) {
      idx = delta > 0 ? -1 : 0;
    }

    do {
      idx = (idx + delta + length) % length;
      const opt = this._options[idx];
      if (!opt.hasAttribute('disabled')) {
        this._setSelectedIndex(idx, { emitInput: false, emitChange: false, reflect: true, focus: true });
        return;
      }
    } while (idx !== start);
  }

  private _focusOption(index: number) {
    const option = this._options[index];
    if (option) {
      option.focus();
    }
  }

  private _handleDocumentMouseDown = (event: MouseEvent) => {
    if (!this._open) {
      return;
    }
    const path = event.composedPath();
    if (!path.includes(this)) {
      this._closeDropdown(false);
    }
  };

  private _onFocusOut = (event: FocusEvent) => {
    if (this._open) {
      const nextFocus = event.relatedTarget as Node | null;
      if (!nextFocus || !this.contains(nextFocus)) {
        this._closeDropdown(false);
      }
    }
  };

  private _onSlotChange = () => {
    this._collectOptions();
    this._syncSelectionFromOptions();
    this._updateComponentState();
  };

  private _getOptionValue(option: OptionElement) {
    return this._deriveOptionValue(option);
  }

  private _updateHelpHover(): void {
    updateShadowDOMHelpHover(this, this._helpHoverEl);
  }
}

customElements.define('as-dropdown', AsDropdown);
